import { IUser } from "authentication/domain/IUser";
import { IUserGroup } from "authentication/domain/IUserGroup";
import { authServices } from "authentication/services/auth.service";
import Modal from "components/Modal/Modal";
import InfoTooltip from "components/Popup/InfoTooltip";
import { IMenuConfiguration } from "Dashboard/domain/IMenuConfiguration";
import { MenuConfiguration } from "Dashboard/domain/MenuConfiguration";
import { CARNET_MODULE_NAME, CARNET_PAYROLL_MODULE_NAME } from "Dashboard/scenes/Carnets/domain/CarnetModule";
import { EXPENSES_MODULE_NAME } from "Dashboard/scenes/Expenses/domain/ExpensesModule";
import { REQUISITION_SITE_MODULE_NAME, REQUISITION_WAREHOUSE_MODULE_NAME } from "Dashboard/scenes/Requisitions/domain/RequisitionsModule";
import { RESOURCES_MODULE_NAME, RESOURCES_VEHICLE_MODULE_NAME } from "Dashboard/scenes/Resources/domain/ResourcesModule";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fullAccessGroup, readGroup, writeGroup } from "utils/authUtil";

interface IProps {
  setClickOutsideActive: Function;
  isOpen: boolean;
  setIsOpen: Function;
  updateUser: Function;
  user: IUser | null;
  availableGroups: IUserGroup[];
}

interface IAvailableModule {
  module: string;
  title: string;
}

const UserGroupsModal = ({
  setClickOutsideActive,
  isOpen,
  setIsOpen,
  updateUser,
  user,
  availableGroups
}: IProps) => {

  const [allModules, setAllModules] = useState<IAvailableModule[]>([]);
  const [readGroups, setReadGroups] = useState<string[]>([]);
  const [writeGroups, setWriteGroups] = useState<string[]>([]);
  const [fullAccessGroups, setFullAccessGroups] = useState<string[]>([]);
  const [allReadGranted, setAllReadGranted] = useState(false);
  const [allWriteGranted, setAllWriteGranted] = useState(false);
  const [allFullAccessGranted, setAllFullAccessGranted] = useState(false);
  const [pendingPermissions, setPendingPermissions] = useState<string[]>([]);
  const [availableGroupNames, setAvailableGroupNames] = useState<string[]>([]);

  useEffect(() => {
    const allModules = getAllModules(MenuConfiguration);
    let allModulesForPermissions: IAvailableModule[] = [];
    let allReadGroups: string[] = [];
    let allWriteGroups: string[] = [];
    let allFullAccessGroups: string[] = [];
    for (let index = 0; index < allModules.length; index++) {
      const module = allModules[index];
      if (module.module === CARNET_MODULE_NAME) {
        allModulesForPermissions.push({ module: CARNET_PAYROLL_MODULE_NAME, title: 'Karneti - zarade' });
        allWriteGroups.push(writeGroup(CARNET_PAYROLL_MODULE_NAME));
      }
      if (module.module === RESOURCES_MODULE_NAME) {
        allModulesForPermissions.push({ module: RESOURCES_VEHICLE_MODULE_NAME, title: 'Resursi - vozila' });
        allWriteGroups.push(writeGroup(RESOURCES_VEHICLE_MODULE_NAME));
      }
      allModulesForPermissions.push(module);
      allReadGroups.push(readGroup(module.module));
      allWriteGroups.push(writeGroup(module.module));
      allFullAccessGroups.push(fullAccessGroup(module.module));
      if (module.module === REQUISITION_WAREHOUSE_MODULE_NAME) {
        allModulesForPermissions.push({ module: REQUISITION_SITE_MODULE_NAME, title: 'Trebovanja - gradilište' });
        allWriteGroups.push(writeGroup(REQUISITION_SITE_MODULE_NAME));
      }
    }
    setAllModules(allModulesForPermissions);
    setReadGroups(allReadGroups.filter(group => availableGroupNames.includes(group)));
    setWriteGroups(allWriteGroups.filter(group => availableGroupNames.includes(group)));
    setFullAccessGroups(allFullAccessGroups.filter(group => availableGroupNames.includes(group)));
  }, [availableGroupNames]);

  useEffect(() => {
    if (availableGroups) {
      setAvailableGroupNames(availableGroups.map(group => group.name));
    }
  }, [availableGroups]);

  useEffect(() => {
    setAllReadGranted(readGroups.every(readGroup => user?.userGroups.find((userGroup) => userGroup.name === readGroup) !== undefined));
    setAllWriteGranted(writeGroups.every(writeGroup => user?.userGroups.find((userGroup) => userGroup.name === writeGroup) !== undefined));
    setAllFullAccessGranted(fullAccessGroups.every(fullAccessGroup => user?.userGroups.find((userGroup) => userGroup.name === fullAccessGroup) !== undefined));
  }, [user, user?.userGroups, readGroups, writeGroups, fullAccessGroups]);

  const closeDialog = () => {
    setIsOpen(false);
    setClickOutsideActive(true);
  };

  const getAllModules = (MenuConfiguration: IMenuConfiguration): IAvailableModule[] => {
    let modules: IAvailableModule[] = [];
    modules.push(...MenuConfiguration.group3.map(item => {
      return {
        module: item.module,
        title: item.title
      };
    }));
    modules.push(...MenuConfiguration.group4.map(item => {
      return {
        module: item.module,
        title: item.title
      };
    }));
    modules.push(...MenuConfiguration.group2.map(item => {
      return {
        module: item.module,
        title: item.title
      };
    }));
    modules.push(...MenuConfiguration.group1.map(item => {
      return {
        module: item.module,
        title: item.title
      };
    }));
    modules.push(...MenuConfiguration.group5.map(item => {
      return {
        module: item.module,
        title: item.title
      };
    }));
    modules.push({ module: EXPENSES_MODULE_NAME, title: 'Troškovi lično' });
    return modules;
  }

  const joinGroups = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (user?.username) {
        const updatedUser = await authServices.joinGroups(user?.username, [e.target.value]);
        toast.success(`Korisnik je uspešno dodat grupi: ${e.target.value}.`);
        setPendingPermissions(pendingPermissions => pendingPermissions.filter(permission => permission !== e.target.value));
        updateUser(updatedUser);
      }
    } catch (err) {
      console.error(err);
    }
  }

  const leaveGroups = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (user?.username) {
        const updatedUser = await authServices.leaveGroups(user?.username, [e.target.value]);
        toast.success(`Korisnik je uklonjen iz grupe: ${e.target.value}.`);
        setPendingPermissions(pendingPermissions => pendingPermissions.filter(permission => permission !== e.target.value));
        updateUser(updatedUser);
      }
    } catch (err) {
      console.error(err);
    }
  }


  const onChangeCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPendingPermissions(pendingPermissions => [...pendingPermissions, e.target.value]);
    if (e.target.checked) {
      joinGroups(e);
    }
    else if (!e.target.checked) {
      leaveGroups(e);
    }
  };


  const onChangeCheckAll = async (e: React.ChangeEvent<HTMLInputElement>, groups: string[]) => {
    try {
      if (user?.username) {
        setPendingPermissions(pendingPermissions => [...pendingPermissions, e.target.value, ...groups]);
        const toastMessageGroupDescription = e.target.value === 'all-read'
          ? 'čitanje'
          : (e.target.value === 'all-write' ? 'pisanje' : 'pun pristup');
        if (e.target.checked) {
          const updatedUser = await authServices.joinGroups(user?.username, groups);
          toast.success(`Korisnik je uspešno dodat svim grupama za ${toastMessageGroupDescription}.`);
          updateUser(updatedUser);
        } else if (!e.target.checked) {
          const updatedUser = await authServices.leaveGroups(user?.username, groups);
          toast.success(`Korisnik je uspešno uklonjen iz svih grupa za ${toastMessageGroupDescription}.`);
          updateUser(updatedUser);
        }
        setPendingPermissions(pendingPermissions => pendingPermissions.filter(permission => permission !== e.target.value && !groups.includes(permission)));
      }
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      title={`Prava pristupa za ${user?.name} (${user?.username})`}
      onClose={closeDialog}
    >
      <div className="user-groups">
        <div className="settings-check">
          <div className="g-grid g-header">
            <div className="row">
              <div className="col lowercase center separator-line">modul</div>
              <div className="col lowercase center separator-line">
                čitanje
                <input type="checkbox"
                  defaultValue={"all-read"}
                  checked={allReadGranted}
                  onChange={(event) => onChangeCheckAll(event, readGroups)}
                  disabled={pendingPermissions.includes("all-read")}
                />
              </div>
              <div className="col lowercase center separator-line">
                čitanje / pisanje
                <input type="checkbox"
                  defaultValue={"all-write"}
                  checked={allWriteGranted}
                  onChange={(event) => onChangeCheckAll(event, writeGroups)}
                  disabled={pendingPermissions.includes("all-write")}
                />
              </div>
              <InfoTooltip tooltip="čitanje, pisanje, brisanje, izmena datuma" classesProp="col lowercase center separator-line">
                pun pristup
                <input type="checkbox"
                  defaultValue={"all-full-access"}
                  checked={allFullAccessGranted}
                  onChange={(event) => onChangeCheckAll(event, fullAccessGroups)}
                  disabled={pendingPermissions.includes("all-full-access")}
                />
              </InfoTooltip>
            </div>
          </div>

          <div className="g-grid g-content">
            {allModules &&
              allModules.map((module, index) => {
                const readRoleValue = readGroup(module.module);
                const writeRoleValue = writeGroup(module.module);
                const fullAccessRoleValue = fullAccessGroup(module.module);
                return (
                  <div className="row" key={index}>
                    <div className="col lowercase right separator-line">{module.title}</div>
                    <div className="col lowercase center separator-line">
                      {availableGroupNames.includes(readRoleValue)
                        ? (<input type="checkbox"
                          defaultValue={readRoleValue}
                          checked={user?.userGroups.find((el) =>
                            el.name === readRoleValue) !== undefined}
                          onChange={onChangeCheck}
                          disabled={pendingPermissions.includes(readRoleValue)}
                        />)
                        : (<></>)}
                    </div>
                    <div className="col lowercase center separator-line">
                      {availableGroupNames.includes(writeRoleValue)
                        ? (<input type="checkbox"
                          defaultValue={writeRoleValue}
                          checked={user?.userGroups.find((el) =>
                            el.name === writeRoleValue) !== undefined}
                          onChange={onChangeCheck}
                          disabled={pendingPermissions.includes(writeRoleValue)}
                        />)
                        : (<></>)}
                    </div>
                    <div className="col lowercase center separator-line">
                      {availableGroupNames.includes(fullAccessRoleValue)
                        ? (<input type="checkbox"
                          defaultValue={fullAccessRoleValue}
                          checked={user?.userGroups.find((el) =>
                            el.name === fullAccessRoleValue) !== undefined}
                          onChange={onChangeCheck}
                          disabled={pendingPermissions.includes(fullAccessRoleValue)}
                        />)
                        : (<></>)}
                    </div>
                  </div>
                );
              }
              )}
          </div>
        </div>
      </div>
    </Modal >
  )
};

export default UserGroupsModal;
